/**
 * Çalışma planı kayıtları (sahibi yazar):
 * - `users/{uid}/study_plan/current`: ayarlar, takvim başlangıcı, işaretlenen görevler, haftalık karneler, YZ yorumu.
 *   Takvim kaydedilmez, her açılışta utils/studyPlanner.ts › buildCalendar ile yeniden hesaplanır.
 * - `users/{uid}/study_plan/progress`: konu ilerlemesi `items.{konuAnahtarı}` = { n: Kapı Notları, p: podcast, t: plan
 *   görevi, m: elle (1/0), at: bitme anı }. Kapı Notları'nda 90 sn kalınca ve podcast sonuna kadar dinlenince
 *   otomatik yazılır (`kpss-study-signal` olayı, App dinler).
 * - `users/{uid}/study_time/{YYYY-MM}`: uygulamada geçen etkin süre `dDD` (saniye, increment; sekme görünür ve son
 *   2 dakikada dokunuş/tuş varsa ya da podcast çalıyorsa), oyun süresi `gDD`, ders ders çözülen soru `qDD.{ders}` ve
 *   doğru `cDD.{ders}` (Gelişim Karnesi'ne giden her cevap: userService › `kpss-questions-answered`), derste geçen etkin süre
 *   `sDD.{ders}` (MainApp o derste açıkken, oyun hariç; ders hızı kalibrasyonu).
 * - `users/{uid}/study_plan/games`: oyun bazında toplam süre / oturum (`modes.{oyun}.sec|n|last`): MainApp oyun
 *   modları ve Kim Bilir. Plan sevdiği oyunları öne alır, PRO yorumunda Kapı Hoca analiz eder.
 * - `study_reminders/{uid}`: hatırlatma saati (çeyrek saate yuvarlı `slot`), günler, sonraki günlerin görev sayısı;
 *   sunucu `studyPlanReminders` (15 dk'da bir) push gönderir. Bugün görev işaretlendiyse göndermez (`lastDoneDay`).
 * Ana kaynak bulut; `localStorage` yalnızca ders seçim ekranındaki kart anında çizilsin diye önbellek.
 */
import { deleteDoc, deleteField, doc, getDoc, increment, onSnapshot, setDoc, updateDoc, FieldPath } from 'firebase/firestore';
import { auth, db } from './firebase';
import type { AiTuning, ExternalSource, ItemProgress, ProgressMap, StudyPlanDoc, StudyTime } from '../utils/studyPlanner';
import type { SubjectType } from '../types';
import { getUserProfile, isPremiumUser } from './userService';
import { getSubjectFromTopic } from '../utils/topicRouting';
import { getState as podcastState } from './podcastPlayer';
import { getStudyContext } from './pomodoro';

const CACHE_KEY = 'kpss_study_plan_v1';
const ADMIN_EMAILS = ['cokunsuleyman@gmail.com', 'necmiyeortky@gmail.com', 'necmiyeort@gmail.com'];
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://europe-west1-tarih-atlas-kpss.cloudfunctions.net';

export const isPlanPro = () => isPremiumUser() || ADMIN_EMAILS.includes((auth.currentUser?.email || '').toLowerCase());

const planRef = (uid: string) => doc(db, 'users', uid, 'study_plan', 'current');
const progressRef = (uid: string) => doc(db, 'users', uid, 'study_plan', 'progress');

export function cachedPlan(): StudyPlanDoc | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw);
        return p && p.uid === auth.currentUser?.uid ? p.plan : null;
    } catch { return null; }
}

function cache(plan: StudyPlanDoc | null) {
    try {
        if (plan) localStorage.setItem(CACHE_KEY, JSON.stringify({ uid: auth.currentUser?.uid, plan }));
        else localStorage.removeItem(CACHE_KEY);
    } catch { /* dolu/kapalı */ }
}

/** Planı dinler; giriş yoksa null. Önbellekte "doküman yok" bulutta yok demek değil: o durumda önbelleğe dokunmaz. */
export function subscribeStudyPlan(cb: (plan: StudyPlanDoc | null, fromServer: boolean) => void): () => void {
    const uid = auth.currentUser?.uid;
    if (!uid) { cb(null, true); return () => { /* yok */ }; }
    const c = cachedPlan();
    if (c) cb(c, false);
    return onSnapshot(planRef(uid), { includeMetadataChanges: true }, snap => {
        const fromServer = !snap.metadata.fromCache;
        if (!snap.exists()) {
            if (fromServer) { cache(null); cb(null, true); }
            return;
        }
        const plan = snap.data() as StudyPlanDoc;
        cache(plan);
        cb(plan, fromServer);
    }, err => console.warn('Çalışma planı okunamadı', err));
}

/** Yazımlar beklenmez (kalıcı önbellekte kuyruğa girer, internetsizken ekran donmasın) */
export function saveStudyPlan(plan: StudyPlanDoc): void {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const next = { ...plan, updatedAt: Date.now() };
    cache(next);
    setDoc(planRef(uid), JSON.parse(JSON.stringify(next))).catch(e => console.error('Çalışma planı kaydedilemedi', e));
}

export function setTaskDone(plan: StudyPlanDoc, taskId: string, done: boolean, finishes?: string[]): StudyPlanDoc {
    const uid = auth.currentUser?.uid;
    const nextDone = { ...(plan.done || {}) };
    if (done) nextDone[taskId] = true; else delete nextDone[taskId];
    const next = { ...plan, done: nextDone, updatedAt: Date.now() };
    cache(next);
    if (uid) {
        updateDoc(planRef(uid), new FieldPath('done', taskId), done ? true : deleteField(), 'updatedAt', next.updatedAt)
            .catch(e => console.error('Görev işaretlenemedi', e));
        if (finishes?.length) setTaskProgress(finishes, done);
        if (done) setDoc(doc(db, 'study_reminders', uid), { lastDoneDay: localDay() }, { merge: true }).catch(() => { /* yok */ });
    }
    return next;
}

export function deleteStudyPlan(): void {
    const uid = auth.currentUser?.uid;
    cache(null);
    if (!uid) return;
    deleteDoc(planRef(uid)).catch(e => console.error('Çalışma planı silinemedi', e));
    setDoc(doc(db, 'study_reminders', uid), { on: false }, { merge: true }).catch(() => { /* yok */ });
}

// ─── Karne ve Zihin Ağı sinyalleri ─────────────────────────────────────────────

const SUBJECTS: SubjectType[] = ['turkish', 'math', 'history', 'geography', 'citizenship'];

/**
 * Planı şekillendiren karne sinyalleri (profilden, yazmadan):
 * - weakUnits: Gelişim Karnesi'nde en az 5 soru görülmüş ve doğruluğu %65'in altında kalan üniteler, en zayıf önce
 *   (plan bu üniteleri Kapı Notları tekrarı + eşdeğerlerle öne alır),
 * - wrongs: Zihin Ağı'ndaki hatalı soru sayısı ders ders (eşiği geçen derse bugün "önce hatanı onar" görevi gelir),
 * - accuracy: ders ders görülen soru ve doğruluk (seviye tespiti: beyan ↔ karne, utils/studyPlanner.ts › levelMismatch).
 */
export function planSignals(): { weakUnits: Partial<Record<SubjectType, string[]>>; wrongs: Partial<Record<SubjectType, number>>; accuracy: Partial<Record<SubjectType, { n: number; acc: number }>> } {
    const p: any = getUserProfile() || {};
    const weakUnits: Partial<Record<SubjectType, string[]>> = {};
    const stats = Object.values(p.stats || {}) as any[];
    for (const s of SUBJECTS) {
        const list = stats.filter(x => x.lesson === s && (x.totalQuestionsSeen || 0) >= 5)
            .map(x => ({ n: String(x.topicName || ''), acc: 1 - (x.wrongAnswers || 0) / x.totalQuestionsSeen }))
            .filter(x => x.n && x.acc < 0.65).sort((a, b) => a.acc - b.acc).map(x => x.n);
        if (list.length) weakUnits[s] = list.slice(0, 6);
    }
    const accuracy: Partial<Record<SubjectType, { n: number; acc: number }>> = {};
    for (const s of SUBJECTS) {
        const st = stats.filter(x => x.lesson === s && (x.totalQuestionsSeen || 0) > 0);
        const n = st.reduce((a, x) => a + (x.totalQuestionsSeen || 0), 0);
        if (n) accuracy[s] = { n, acc: 1 - st.reduce((a, x) => a + (x.wrongAnswers || 0), 0) / n };
    }
    const wrongs: Partial<Record<SubjectType, number>> = {};
    for (const list of Object.values(p.wrongQuestions || {}) as any[]) {
        for (const q of (Array.isArray(list) ? list : [])) {
            const s = (q?.subject && SUBJECTS.includes(q.subject) ? q.subject : getSubjectFromTopic(q?.topic || '')) as SubjectType | null;
            if (s) wrongs[s] = (wrongs[s] || 0) + 1;
        }
    }
    return { weakUnits, wrongs, accuracy };
}

// ─── Konu ilerlemesi ───────────────────────────────────────────────────────────

export function subscribeProgress(cb: (p: ProgressMap) => void): () => void {
    const uid = auth.currentUser?.uid;
    if (!uid) { cb({}); return () => { /* yok */ }; }
    return onSnapshot(progressRef(uid), snap => cb(((snap.data() || {}).items || {}) as ProgressMap), err => console.warn('İlerleme okunamadı', err));
}

function writeItem(key: string, patch: Partial<Record<keyof ItemProgress, unknown>>) {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const data: Record<string, unknown> = {};
    for (const [f, v] of Object.entries(patch)) data[f] = v;
    setDoc(progressRef(uid), { items: { [key]: data } }, { merge: true }).catch(e => console.error('İlerleme yazılamadı', e));
}

/** Elle tik: true bitti, false bitmedi (otomatik işaretleri de geçersiz kılar), null eski hâline (otomatik) döner */
export function setManualItem(key: string, value: boolean | null, current?: ItemProgress) {
    if (value === null) { writeItem(key, { m: deleteField() }); return; }
    writeItem(key, { m: value ? 1 : 0, at: value ? (current?.at || Date.now()) : deleteField() });
}

function setTaskProgress(keys: string[], done: boolean) {
    const now = Date.now();
    for (const k of keys) writeItem(k, done ? { t: now, at: now } : { t: deleteField() });
}

/** Kapı Notları / podcast sinyali: alt konu adından konu bulunur (plan modülü ayrı parça olarak iner) */
export async function recordStudySignal(subject: SubjectType, title: string, kind: 'n' | 'p') {
    if (!auth.currentUser?.uid || !subject || !title) return;
    const m = await import('../utils/studyPlanner');
    const item = m.findItem(subject, title);
    if (!item) return;
    const now = Date.now();
    writeItem(item.key, { [kind]: now, at: now } as any);
}

let signalsInstalled = false;
/** App açılışında bir kez: bileşenlerin yaydığı `kpss-study-signal` olaylarını kaydeder */
export function installStudySignals() {
    if (signalsInstalled || typeof window === 'undefined') return;
    signalsInstalled = true;
    window.addEventListener('kpss-study-signal', (e: Event) => {
        const d = (e as CustomEvent).detail || {};
        if (d.subject && d.title && (d.kind === 'n' || d.kind === 'p')) recordStudySignal(d.subject, d.title, d.kind).catch(() => { /* yok */ });
    });
    // Biten pomodoro (hedef: sınava kadar pomodoro döngüsü)
    window.addEventListener('kpss-pomodoro-done', () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        const d = new Date();
        setDoc(doc(db, 'users', uid, 'study_time', monthKey(d)), { [`p${String(d.getDate()).padStart(2, '0')}`]: increment(1) }, { merge: true }).catch(() => { /* yok */ });
    });
    // Çözülen sorular (ders ders, günlük): plan görevlerini karneden kendiliğinden işaretlemek için
    window.addEventListener('kpss-questions-answered', (e: Event) => {
        const items = ((e as CustomEvent).detail || []) as { subject: string; correct: boolean }[];
        const uid = auth.currentUser?.uid;
        if (!uid || !items.length) return;
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, '0');
        const q: Record<string, unknown> = {}, c: Record<string, unknown> = {};
        const byS: Record<string, { n: number; c: number }> = {};
        for (const it of items) { const x = byS[it.subject] || (byS[it.subject] = { n: 0, c: 0 }); x.n++; if (it.correct) x.c++; }
        for (const [sub, v] of Object.entries(byS)) { q[sub] = increment(v.n); if (v.c) c[sub] = increment(v.c); }
        setDoc(doc(db, 'users', uid, 'study_time', monthKey(d)), { [`q${dd}`]: q, ...(Object.keys(c).length ? { [`c${dd}`]: c } : {}) }, { merge: true })
            .catch(e2 => console.warn('Soru sayacı yazılamadı', e2));
    });
}

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Oyun oturumu bitti (MainApp oyun modu, Kim Bilir): 20 sn altı sayılmaz */
export function recordGameTime(mode: string, sec: number) {
    const uid = auth.currentUser?.uid;
    if (!uid || !mode || mode === 'explore' || sec < 20) return;
    const s2 = Math.min(Math.round(sec), 4 * 3600);
    const d = new Date();
    setDoc(doc(db, 'users', uid, 'study_plan', 'games'), { modes: { [mode]: { sec: increment(s2), n: increment(1), last: Date.now() } } }, { merge: true }).catch(() => { /* yok */ });
    setDoc(doc(db, 'users', uid, 'study_time', monthKey(d)), { [`g${String(d.getDate()).padStart(2, '0')}`]: increment(s2) }, { merge: true }).catch(() => { /* yok */ });
}

export interface GameStat { sec: number; n: number; last?: number }
export async function loadGameStats(): Promise<Record<string, GameStat>> {
    const uid = auth.currentUser?.uid;
    if (!uid) return {};
    try { return ((await getDoc(doc(db, 'users', uid, 'study_plan', 'games'))).data()?.modes || {}) as Record<string, GameStat>; } catch { return {}; }
}

/** Bileşenler için: Kapı Notları okundu (n) / podcast bitti (p) */
export const emitStudySignal = (subject: string | undefined, title: string | undefined, kind: 'n' | 'p') => {
    if (!subject || !title) return;
    try { window.dispatchEvent(new CustomEvent('kpss-study-signal', { detail: { subject, title, kind } })); } catch { /* yok */ }
};

// ─── Uygulamada geçen süre ─────────────────────────────────────────────────────

const localDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const TICK_SEC = 15;
const IDLE_MS = 2 * 60 * 1000;
const FLUSH_MS = 60 * 1000;
let trackerOn = false;

/** Etkin süre sayacı (App, giriş yapılmışken bir kez başlatır) */
export function startAppTimeTracker() {
    if (trackerOn || typeof window === 'undefined') return;
    trackerOn = true;
    let lastInput = Date.now();
    let pending = 0;
    let pendingSubj: Record<string, number> = {};
    let lastFlush = Date.now();
    const bump = () => { lastInput = Date.now(); };
    ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'].forEach(ev => window.addEventListener(ev, bump, { passive: true, capture: true }));
    const flush = () => {
        const uid = auth.currentUser?.uid;
        if (!uid || pending <= 0) return;
        const d = new Date();
        const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const field = `d${String(d.getDate()).padStart(2, '0')}`;
        const sec = pending;
        const subj = pendingSubj;
        pending = 0;
        pendingSubj = {};
        lastFlush = Date.now();
        const subjData = Object.fromEntries(Object.entries(subj).map(([k, v]) => [k, increment(v)]));
        setDoc(doc(db, 'users', uid, 'study_time', month), { [field]: increment(sec), ...(Object.keys(subj).length ? { [`s${field.slice(1)}`]: subjData } : {}), updatedAt: Date.now() }, { merge: true })
            .catch(() => { pending += sec; });
    };
    window.setInterval(() => {
        const podcastPlaying = podcastState().playing; // arka planda podcast dinlemek de çalışma
        const active = document.visibilityState === 'visible' && Date.now() - lastInput < IDLE_MS;
        if (auth.currentUser && (active || podcastPlaying)) {
            pending += TICK_SEC;
            const c = getStudyContext();
            if (active && c.subject && !c.inGame) pendingSubj[c.subject] = (pendingSubj[c.subject] || 0) + TICK_SEC;
        }
        if (Date.now() - lastFlush >= FLUSH_MS) flush();
    }, TICK_SEC * 1000);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
}

/** Son iki ayın gün gün sayaçları (YYYY-MM-DD anahtarlı): etkin süre, oyun süresi, ders ders soru / doğru */
export async function loadStudyTime(): Promise<StudyTime> {
    const out: StudyTime = { sec: {}, game: {}, q: {}, c: {}, subj: {}, pomo: {} };
    const uid = auth.currentUser?.uid;
    if (!uid) return out;
    const now = new Date();
    for (const back of [1, 0]) {
        const month = monthKey(new Date(now.getFullYear(), now.getMonth() - back, 1));
        try {
            const data = (await getDoc(doc(db, 'users', uid, 'study_time', month))).data() || {};
            for (const [k, v] of Object.entries(data)) {
                const m = /^([dgqcsp])(\d\d)$/.exec(k);
                if (!m) continue;
                const day = `${month}-${m[2]}`;
                if (m[1] === 'd') out.sec[day] = Number(v) || 0;
                else if (m[1] === 'g') out.game[day] = Number(v) || 0;
                else if (m[1] === 's') out.subj![day] = (v || {}) as any;
                else if (m[1] === 'p') out.pomo![day] = Number(v) || 0;
                else (m[1] === 'q' ? out.q : out.c)[day] = (v || {}) as any;
            }
        } catch { /* çevrimdışı */ }
    }
    return out;
}

// ─── Hatırlatma ────────────────────────────────────────────────────────────────

/** Hatırlatma kaydı: saat çeyreğe yuvarlanır; önümüzdeki 14 günün görev sayısı push metninde kullanılır */
export function syncReminder(plan: StudyPlanDoc, counts: Record<string, number>) {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const cfg = plan.config;
    const [h, m] = (cfg.reminderTime || '20:00').split(':').map(Number);
    const q = Math.round(((h || 0) * 60 + (m || 0)) / 15) * 15 % (24 * 60);
    const slot = `${String(Math.floor(q / 60)).padStart(2, '0')}:${String(q % 60).padStart(2, '0')}`;
    const days = (cfg.dayHours || []).map((x, i) => (x > 0 ? i : -1)).filter(i => i >= 0);
    setDoc(doc(db, 'study_reminders', uid), { on: cfg.reminderOn !== false, slot, days, counts, examDate: cfg.examDate, updatedAt: Date.now() }, { merge: true })
        .catch(e => console.warn('Hatırlatma kaydedilemedi', e));
}

// ─── PRO: Kapı Hoca yorumu (YZ) ────────────────────────────────────────────────

/**
 * YZ'nin ipucunda önerebileceği özellikler ve gerçekte ne yaptıkları (2026-10-29: yorum "Stüdyo'dan podcast" gibi
 * var olmayan yollar öneriyordu). Özellik eklenir ya da değişirse burayı da güncelle.
 */
const APP_FEATURES_FOR_AI = [
    'Kapı Notları: ders ekranının üst çubuğu › ünite → alt konu notu (bütün dersler)',
    'Kapı Slaytları: ünite sunumu, yalnız Tarih / Coğrafya / Vatandaşlık',
    'Podcast: Çalışma Alanı › Podcast; hazır alt konu podcast\'leri, arka planda çalar (yalnız Tarih / Coğrafya / Vatandaşlık)',
    'Kapı Stüdyo (PRO): ders seçim ekranındaki +; YouTube videosu / PDF / defter fotoğrafından 15 dakikalık bölüm notu + test, video denemesi; paket sayfasında bölümlerden podcast',
    'Harita: Tarih ve Coğrafya ders ekranı; alt konunun haritası, konum kartları, Tarih\'te tarihi şahıslarla sohbet; Coğrafya\'da Fiziki / Yağış / Deprem / Demiryolları / Koordinat katmanları ve dilsiz harita',
    'Zihin şeması: Vatandaşlık konu şemaları',
    'Çıkmış Sorular: konu süzgeçli çıkmış soru eşdeğerleri; PRO\'da "6 benzer" (çıkabilecek sorular)',
    'Deneme Merkezi: Kişisel YZ denemesi, Kapı Bank, Konu Tarama (Lokal), Türkiye Geneli branş denemesi, Güncel Bilgiler (Vatandaşlık), Eski Denemelerim, Yıldızlar Karması (ünite ünite en çok beğenilen sorular; PRO\'da kendi favorilerinden deneme)',
    'Türkiye Geneli Dijital Deneme: 120 soruluk tam kitapçık, Türkiye sırası',
    'Zihin Ağı: yanlışlar konu düğümlerinde, tekrar çöz; ➕ Hata Ekle (kitap sorusunun fotoğrafı); PRO\'da Hata Onarım denemesi',
    'Gelişim Karnesi (Analiz): ders ve ünite doğrulukları',
    'Cezeri (PRO sesli koç): Tarih / Coğrafya / Vatandaşlık konusunu sesli anlatır, soru-cevap yapar',
    'Oyun Alanı: Kim Bilir? (kuş yarışı dahil), Milyoner, 3\'te 3, Düello, Beyin Flaşı, Hafıza Kartı, Harita eşleştirme ve Sapan Modu (Coğrafya), Bölge Fatihi, Şehir Avcısı, Gizemli Seyyah, Yol Arkadaşım, Çark Modu, Ajan 81/600, TÜİK Verileri, Seçim Sandığı, Dilbilgisi Kuşu',
    'Pomodoro: görevdeki 🍅 ile 25 + 5 dk',
].map(x => `  • ${x}`).join('\n');

/** 3.8 Flash'a planın özeti gider; dönen ağırlıklar ve odak üniteler takvime işlenir. Haftada bir. */
export async function requestAiTuning(summary: string): Promise<AiTuning> {
    const user = auth.currentUser;
    if (!user) throw new Error('Giriş gerekli');
    const token = await user.getIdToken();
    const prompt = `Sen KPSS hazırlık koçu "Kapı Hoca"sın. Aşağıda bir öğrencinin çalışma planı ayarları, uygulama dışı kaynakları, konu ilerlemesi, haftalık karneleri ve Gelişim Karnesi (ders/ünite doğruluk) özeti var.
Görevin: gerçekçi, kısa ve motive edici bir değerlendirme yaz ve gelecek hafta(lar) için planı ayarla.
Kurallar:
- Türkçe, samimi ama net; "sen" diye hitap et. Mesaj en çok 600 karakter.
- Uygulama dışında beyan ettiği saatleri hesaba kat: uygulamada geçirdiği süre azsa ama dış kaynak çoksa bunu eksiklik sayma.
- Doğruluğu düşük ya da geride kalan derslerin ağırlığını artır (1,0–1,6), iyi gidenleri azalt (0,7–1,0). Ağırlık verilmeyen ders 1 sayılır.
- Odak üniteler yalnızca verilen ünite adlarından seçilsin (aynen), en çok 6.
- 2–4 kısa ipucu, YALNIZ aşağıdaki özelliklerle ve yalnız yazdığı işle (olmayan bir özellik ya da yol uydurma):
${APP_FEATURES_FOR_AI}
- Hedef puana ulaşmak için gereken haftalık saat ile öğrencinin gerçekte çalıştığı süreyi (uygulama + beyan ettiği dış kaynak) karşılaştır:
  "dayHours" ile gün gün toplam saat öner (Pzt değil, dizi sırası PAZAR'dan başlar: [Paz, Pzt, Sal, Çar, Per, Cum, Cmt]; 0 = izin; her gün en çok 12).
  Öğrenci sürekli az çalışıyorsa gerçekçi biçimde azalt ve bunu mesajda açıkça söyle (hedefin riskini de); yetişmiyorsa ve uyuyorsa artır.
  Değişiklik gerekmiyorsa dayHours'u gönderme. Azaltırken birden kesme: gün başı haftada en çok %15 (kod da bunu uygular). Dış kaynak (dershane/YouTube/kitap) saatlerini yalnızca karneler belirgin farklıysa "external" ile değiştir.
- Oyun Alanı: oyun sürelerine bak; ders çalışmayı engelliyorsa "gameMinutes" (günlük mola, 0–45) azalt, iyi gidiyorsa sevdiği oyunları "favoriteGames" ile (oyun kimlikleri aynen, en çok 4) öne al.
JSON döndür: {"message": string, "weights": {"turkish"?: number, "math"?: number, "history"?: number, "geography"?: number, "citizenship"?: number}, "focus": [{"subject": "turkish"|"math"|"history"|"geography"|"citizenship", "unit": string, "reason": string}], "tips": [string], "dayHours"?: [number,number,number,number,number,number,number], "external"?: [{"kind": "youtube"|"dershane"|"kitap"|"ozel"|"diger", "hoursPerWeek": number}], "gameMinutes"?: number, "favoriteGames"?: [string]}

${summary}`;
    const res = await fetch(`${BACKEND_URL}/generateJson`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt, schemaType: 'none', model: 'gemini-3.8-flash', temperature: 0.6 }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `Sunucu hatası ${res.status}`);
    const d = body.data || {};
    const subjects: SubjectType[] = ['turkish', 'math', 'history', 'geography', 'citizenship'];
    const weights: Partial<Record<SubjectType, number>> = {};
    for (const s of subjects) { const w = Number(d.weights?.[s]); if (Number.isFinite(w)) weights[s] = Math.max(0.6, Math.min(1.8, w)); }
    return {
        at: Date.now(),
        message: String(d.message || '').slice(0, 800),
        weights,
        focus: (Array.isArray(d.focus) ? d.focus : []).filter((f: any) => subjects.includes(f?.subject) && typeof f?.unit === 'string').slice(0, 6)
            .map((f: any) => ({ subject: f.subject, unit: f.unit.slice(0, 120), reason: String(f.reason || '').slice(0, 200) })),
        tips: (Array.isArray(d.tips) ? d.tips : []).map((t: any) => String(t).slice(0, 200)).slice(0, 4),
        ...(Array.isArray(d.dayHours) && d.dayHours.length === 7 && d.dayHours.every((h: any) => Number.isFinite(Number(h)))
            ? { dayHours: d.dayHours.map((h: any) => Math.max(0, Math.min(12, Math.round(Number(h) * 2) / 2))) } : {}),
        ...(Array.isArray(d.external) ? { external: d.external.filter((e: any) => ['youtube', 'dershane', 'kitap', 'ozel', 'diger'].includes(e?.kind) && Number.isFinite(Number(e.hoursPerWeek)))
            .map((e: any) => ({ kind: e.kind, hoursPerWeek: Math.max(0, Math.min(60, Math.round(Number(e.hoursPerWeek) * 2) / 2)) })) as ExternalSource[] } : {}),
        ...(Number.isFinite(Number(d.gameMinutes)) ? { gameMinutes: Math.max(0, Math.min(45, Math.round(Number(d.gameMinutes) / 5) * 5)) } : {}),
        ...(Array.isArray(d.favoriteGames) ? { favoriteGames: d.favoriteGames.map((g: any) => String(g).slice(0, 40)).slice(0, 4) } : {}),
    };
}
